<template>
  <div id="mainDiv" class="rtl">

    <!-- ── NAVBAR ─────────────────────────────────────────────── -->
    <nav v-if="!hideNav" class="navbar navbar-light">
      <div class="navBarTitle">
        <img src="../static/img/11.png" alt="" />
      </div>

      <a v-if="wazeLink" :href="wazeLink" target="_blank" rel="noopener" class="navbar-waze">
        <img src="../static/img/waze.png" alt="Waze" style="height: 40px;" />
      </a>

      <button class="menu-trigger" @click="isOpen = !isOpen" :aria-expanded="isOpen">
        <svg width="22" height="22" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
        </svg>
      </button>

      <!-- Dropdown menu -->
      <transition name="menu-fade">
        <div v-if="isOpen" class="app-menu-overlay" @click.self="isOpen = false">
          <div class="app-menu-card">
            <button class="app-menu-item" @click="restartGame">
              <span class="app-menu-icon">🏠</span>
              <span class="app-menu-label">{{ t.restart }}</span>
            </button>
            <div class="app-menu-divider"></div>
            <button class="app-menu-item" @click="reopenGame">
              <span class="app-menu-icon">🔄</span>
              <span class="app-menu-label">{{ t.reopen }}</span>
            </button>
            <div class="app-menu-divider"></div>
            <button class="app-menu-item" @click="isOpen = false; showInfo = true">
              <span class="app-menu-icon">ℹ️</span>
              <span class="app-menu-label">{{ t.info }}</span>
            </button>
          </div>
        </div>
      </transition>
    </nav>

    <!-- ── INFO MODAL ─────────────────────────────────────────── -->
    <transition name="modal-fade">
      <div v-if="showInfo" class="info-overlay" @click.self="showInfo = false">
        <div class="info-modal" :dir="isRtl ? 'rtl' : 'ltr'">

          <div class="info-modal-header">
            <span class="info-modal-title">{{ t.infoTitle }}</span>
            <button class="info-modal-close" @click="showInfo = false">✕</button>
          </div>

          <div class="info-modal-body">

            <!-- Phase 1 : Navigation -->
            <div class="accordion-section">
              <button class="accordion-trigger" :class="{ open: activeSection === 'nav' }"
                @click="activeSection = activeSection === 'nav' ? null : 'nav'">
                <span>🗺️ {{ t.phaseNav }}</span>
                <svg class="accordion-chevron" :class="{ rotated: activeSection === 'nav' }"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <transition name="accordion">
                <div v-if="activeSection === 'nav'" class="accordion-content">
                  <div v-for="item in t.navItems" :key="item.icon" class="info-item">
                    <div class="info-item-icon">{{ item.icon }}</div>
                    <div class="info-item-body">
                      <div class="info-item-title">{{ item.title }}</div>
                      <div class="info-item-desc">{{ item.desc }}</div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Phase 2 : Information -->
            <div class="accordion-section">
              <button class="accordion-trigger" :class="{ open: activeSection === 'info' }"
                @click="activeSection = activeSection === 'info' ? null : 'info'">
                <span>💡 {{ t.phaseInfo }}</span>
                <svg class="accordion-chevron" :class="{ rotated: activeSection === 'info' }"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <transition name="accordion">
                <div v-if="activeSection === 'info'" class="accordion-content">
                  <div v-for="item in t.infoItems" :key="item.icon" class="info-item">
                    <div class="info-item-icon">{{ item.icon }}</div>
                    <div class="info-item-body">
                      <div class="info-item-title">{{ item.title }}</div>
                      <div class="info-item-desc">{{ item.desc }}</div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Phase 3 : Exercice -->
            <div class="accordion-section">
              <button class="accordion-trigger" :class="{ open: activeSection === 'ex' }"
                @click="activeSection = activeSection === 'ex' ? null : 'ex'">
                <span>🎯 {{ t.phaseEx }}</span>
                <svg class="accordion-chevron" :class="{ rotated: activeSection === 'ex' }"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <transition name="accordion">
                <div v-if="activeSection === 'ex'" class="accordion-content">
                  <div v-for="item in t.exItems" :key="item.icon" class="info-item">
                    <div class="info-item-icon">{{ item.icon }}</div>
                    <div class="info-item-body">
                      <div class="info-item-title">{{ item.title }}</div>
                      <div class="info-item-desc">{{ item.desc }}</div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

          </div>
        </div>
      </div>
    </transition>

    <div>
      <router-view></router-view>
    </div>
  </div>
</template>

<script>
const TRANSLATIONS = {
  fr: {
    restart: "Recommencer le jeu",
    reopen: "Retourner au jeu",
    info: "Comment jouer ?",
    infoTitle: "Comment jouer ?",
    phaseNav: "Phase 1 — Navigation",
    phaseInfo: "Phase 2 — Information",
    phaseEx: "Phase 3 — Exercice",
    navItems: [
      { icon: "🖼️", title: "Chercher l'image", desc: "Tu vois une liste de photos. Rends-toi à l'endroit indiqué sur la carte, puis choisit la photo qui correspond à ce que tu vois depuis ce point. Bonne réponse : +5 pts. Mauvaise réponse : -1 pt." },
      { icon: "🔤", title: "Mot manquant", desc: "Une image te montre un panneau ou une inscription avec un mot caché. Rends-toi sur place, trouve le mot manquant et tape-le dans le champ. Bonne réponse : +5 pts. Passer le point : -20 pts." },
      { icon: "📍", title: "Carte GPS", desc: "Une carte interactive s'affiche. Elle montre ta position en temps réel. Déplace-toi physiquement jusqu'au point indiqué. Quand tu es assez proche, le jeu passe automatiquement à l'étape suivante." },
    ],
    infoItems: [
      { icon: "💡", title: "Texte informatif", desc: "Une fois arrivé au point, lis les informations sur le lieu. Tu peux prendre le temps de tout lire avant de passer aux questions. Clique sur le bouton pour continuer." },
      { icon: "🎬", title: "Vidéo", desc: "Une vidéo liée au lieu s'affiche. Regarde-la avant de passer aux exercices. Tu peux ajuster la luminosité avec le curseur." },
      { icon: "📷", title: "Réalité Augmentée (AR)", desc: "Oriente ton téléphone dans la direction indiquée par la boussole. Quand tu pointes dans la bonne direction, une animation apparaît sur l'écran. Trouve le mot-clé affiché en haut pour t'aider." },
    ],
    exItems: [
      { icon: "❓", title: "Questions", desc: "Une question s'affiche avec 4 réponses possibles. Choisis la bonne réponse. Bonne réponse : +5 pts. Mauvaise réponse : -1 pt. Plusieurs questions peuvent se suivre." },
      { icon: "🔗", title: "Association (Multiple Match)", desc: "Des images sont affichées d'un côté, des textes de l'autre. Clique sur une image puis sur le texte qui lui correspond. Bonne association : +1 pt. Mauvaise association : -1 pt." },
      { icon: "🧩", title: "Puzzle", desc: "Une image est découpée en 9 morceaux mélangés. Clique sur deux pièces pour les échanger. Reconstitue l'image complète pour valider l'exercice." },
      { icon: "🃏", title: "Memory", desc: "Des cartes sont retournées face cachée. Clique sur deux cartes pour les retourner. Si elles forment une paire, elles restent visibles. Retrouve toutes les paires en un minimum de coups pour maximiser ton score." },
      { icon: "📸", title: "Photo", desc: "Suis les instructions affichées et prends une photo de l'endroit ou de l'objet demandé. La photo sera validée par l'organisateur." },
    ],
  },
  he: {
    restart: "להתחיל את המשחק מהתחלה",
    reopen: "לחזור למשחק",
    info: "איך משחקים?",
    infoTitle: "איך משחקים?",
    phaseNav: "שלב 1 — ניווט",
    phaseInfo: "שלב 2 — מידע",
    phaseEx: "שלב 3 — תרגיל",
    navItems: [
      { icon: "🖼️", title: "חפשו את התמונה", desc: "מוצגות כמה תמונות. הגיעו למקום המסומן במפה ובחרו את התמונה שתואמת את מה שאתם רואים מהנקודה. תשובה נכונה: +5 נקודות. תשובה שגויה: -1 נקודה." },
      { icon: "🔤", title: "מילה חסרה", desc: "תמונה מציגה שלט עם מילה מוסתרת. הגיעו למקום, מצאו את המילה החסרה והקלידו אותה. תשובה נכונה: +5 נקודות. דילוג על הנקודה: -20 נקודות." },
      { icon: "📍", title: "מפה GPS", desc: "מוצגת מפה אינטראקטיבית עם מיקומכם בזמן אמת. התקדמו פיזית לעבר הנקודה המסומנת. כשתהיו קרובים מספיק, המשחק יעבור אוטומטית לשלב הבא." },
    ],
    infoItems: [
      { icon: "💡", title: "מידע על המקום", desc: "לאחר הגעה לנקודה, קראו את המידע על המקום. קחו את הזמן לקרוא הכל לפני שממשיכים לשאלות. לחצו על הכפתור כדי להמשיך." },
      { icon: "🎬", title: "וידאו", desc: "מוצג סרטון הקשור למקום. צפו בו לפני המעבר לתרגילים. ניתן לכוונן את הבהירות עם המחוון." },
      { icon: "📷", title: "מציאות רבודה (AR)", desc: "כוונו את הטלפון לכיוון המוצג על ידי המצפן. כשתצביעו לכיוון הנכון, תופיע אנימציה על המסך. המילה המפתח בראש המסך תעזור לכם לאתר את הכיוון." },
    ],
    exItems: [
      { icon: "❓", title: "שאלות", desc: "מוצגת שאלה עם 4 תשובות אפשריות. בחרו את התשובה הנכונה. תשובה נכונה: +5 נקודות. תשובה שגויה: -1 נקודה. יכולות להיות כמה שאלות ברצף." },
      { icon: "🔗", title: "התאמה", desc: "מוצגות תמונות מצד אחד וטקסטים מהצד השני. לחצו על תמונה ואחר כך על הטקסט המתאים לה. התאמה נכונה: +1 נקודה. התאמה שגויה: -1 נקודה." },
      { icon: "🧩", title: "פאזל", desc: "תמונה מחולקת ל-9 חלקים מעורבבים. לחצו על שני חלקים כדי להחליף ביניהם. הרכיבו את התמונה המלאה כדי לסיים את התרגיל." },
      { icon: "🃏", title: "זיכרון (Memory)", desc: "קלפים מונחים הפוכים. לחצו על שני קלפים לגלות אותם. אם הם תואמים זה לזה, הם יישארו גלויים. מצאו את כל הזוגות במינימום מהלכים לציון מקסימלי." },
      { icon: "📸", title: "צילום", desc: "עקבו אחר ההוראות המוצגות וצלמו את המקום או העצם המבוקש. הצילום יאושר על ידי המארגן." },
    ],
  },
  en: {
    restart: "Restart the game",
    reopen: "Return to game",
    info: "How to play?",
    infoTitle: "How to play?",
    phaseNav: "Phase 1 — Navigation",
    phaseInfo: "Phase 2 — Information",
    phaseEx: "Phase 3 — Exercise",
    navItems: [
      { icon: "🖼️", title: "Find the image", desc: "A list of photos is shown. Go to the location marked on the map, then choose the photo that matches what you see from that point. Correct: +5 pts. Wrong: -1 pt." },
      { icon: "🔤", title: "Missing word", desc: "An image shows a sign with a hidden word. Go there, find the missing word and type it in. Correct: +5 pts. Skip the point: -20 pts." },
      { icon: "📍", title: "GPS map", desc: "An interactive map shows your real-time position. Move physically towards the marked point. When you're close enough, the game automatically moves to the next step." },
    ],
    infoItems: [
      { icon: "💡", title: "Information text", desc: "Once you arrive at the point, read the information about the place. Take time to read everything before moving to the questions. Tap the button to continue." },
      { icon: "🎬", title: "Video", desc: "A video related to the location is shown. Watch it before moving to the exercises. You can adjust brightness with the slider." },
      { icon: "📷", title: "Augmented Reality (AR)", desc: "Point your phone in the direction shown by the compass. When you face the right direction, an animation appears on screen. The keyword at the top helps you find the direction." },
    ],
    exItems: [
      { icon: "❓", title: "Questions", desc: "A question is shown with 4 possible answers. Choose the correct one. Correct: +5 pts. Wrong: -1 pt. Multiple questions may follow one another." },
      { icon: "🔗", title: "Matching", desc: "Images are shown on one side and texts on the other. Tap an image then the matching text. Correct match: +1 pt. Wrong match: -1 pt." },
      { icon: "🧩", title: "Puzzle", desc: "An image is split into 9 shuffled pieces. Tap two pieces to swap them. Reassemble the full image to complete the exercise." },
      { icon: "🃏", title: "Memory", desc: "Cards are placed face down. Tap two cards to flip them. If they match, they stay visible. Find all pairs in as few moves as possible to maximise your score." },
      { icon: "📸", title: "Photo", desc: "Follow the on-screen instructions and take a photo of the requested place or object. The photo will be validated by the organiser." },
    ],
  },
  ar: {
    restart: "ابدأ اللعبة من الصفر",
    reopen: "العودة إلى اللعبة",
    info: "كيف تلعب؟",
    infoTitle: "كيف تلعب؟",
    phaseNav: "المرحلة 1 — التنقل",
    phaseInfo: "المرحلة 2 — المعلومات",
    phaseEx: "المرحلة 3 — التمرين",
    navItems: [
      { icon: "🖼️", title: "ابحث عن الصورة", desc: "تُعرض قائمة من الصور. توجّه إلى المكان المحدد على الخريطة، ثم اختر الصورة التي تتوافق مع ما تراه من هذه النقطة. إجابة صحيحة: +5 نقاط. إجابة خاطئة: -1 نقطة." },
      { icon: "🔤", title: "الكلمة المفقودة", desc: "تُظهر صورة لافتة بكلمة مخفية. توجّه إلى المكان وابحث عن الكلمة المفقودة واكتبها. إجابة صحيحة: +5 نقاط. تخطّي النقطة: -20 نقطة." },
      { icon: "📍", title: "خريطة GPS", desc: "تُعرض خريطة تفاعلية مع موقعك الفعلي. تحرّك نحو النقطة المحددة. عندما تكون قريباً بما يكفي، تنتقل اللعبة تلقائياً إلى الخطوة التالية." },
    ],
    infoItems: [
      { icon: "💡", title: "نص معلوماتي", desc: "بعد الوصول إلى النقطة، اقرأ المعلومات حول المكان. اضغط على الزر للمتابعة." },
      { icon: "🎬", title: "فيديو", desc: "يُعرض مقطع فيديو متعلق بالمكان. شاهده قبل الانتقال إلى التمارين." },
      { icon: "📷", title: "الواقع المعزّز (AR)", desc: "وجّه هاتفك نحو الاتجاه الذي يحدده البوصلة. عند التوجه الصحيح، تظهر رسوم متحركة على الشاشة." },
    ],
    exItems: [
      { icon: "❓", title: "أسئلة", desc: "يُعرض سؤال مع 4 إجابات محتملة. اختر الإجابة الصحيحة. صحيح: +5 نقاط. خطأ: -1 نقطة." },
      { icon: "🔗", title: "مطابقة", desc: "صور من جهة ونصوص من الجهة الأخرى. اضغط على صورة ثم على النص المناسب لها. مطابقة صحيحة: +1 نقطة. خاطئة: -1 نقطة." },
      { icon: "🧩", title: "بازل", desc: "صورة مقسّمة إلى 9 قطع مخلوطة. اضغط على قطعتين لتبادلهما. أعد تركيب الصورة الكاملة لإتمام التمرين." },
      { icon: "🃏", title: "لعبة الذاكرة", desc: "بطاقات مقلوبة. اضغط على بطاقتين لكشفهما. إذا تطابقتا تبقيان ظاهرتين. ابحث عن جميع الأزواج بأقل عدد من الحركات." },
      { icon: "📸", title: "صورة", desc: "اتّبع التعليمات والتقط صورة للمكان أو الشيء المطلوب." },
    ],
  },
  de: {
    restart: "Spiel neu starten",
    reopen: "Zurück zum Spiel",
    info: "Wie spielt man?",
    infoTitle: "Wie spielt man?",
    phaseNav: "Phase 1 — Navigation",
    phaseInfo: "Phase 2 — Information",
    phaseEx: "Phase 3 — Übung",
    navItems: [
      { icon: "🖼️", title: "Bild finden", desc: "Eine Liste von Fotos wird angezeigt. Gehe zum markierten Ort auf der Karte und wähle das Foto, das deiner Sicht von diesem Punkt entspricht. Richtig: +5 Pkt. Falsch: -1 Pkt." },
      { icon: "🔤", title: "Fehlendes Wort", desc: "Ein Bild zeigt ein Schild mit einem versteckten Wort. Gehe dorthin, finde das fehlende Wort und tippe es ein. Richtig: +5 Pkt. Punkt überspringen: -20 Pkt." },
      { icon: "📍", title: "GPS-Karte", desc: "Eine interaktive Karte zeigt deinen Echtzeit-Standort. Bewege dich zum markierten Punkt. Wenn du nah genug bist, geht das Spiel automatisch weiter." },
    ],
    infoItems: [
      { icon: "💡", title: "Informationstext", desc: "Lies die Informationen über den Ort, sobald du angekommen bist. Drücke die Schaltfläche zum Fortfahren." },
      { icon: "🎬", title: "Video", desc: "Ein Video zum Ort wird angezeigt. Schau es dir an, bevor du zu den Übungen gehst." },
      { icon: "📷", title: "Augmented Reality (AR)", desc: "Richte dein Telefon in die vom Kompass angezeigte Richtung. Wenn du richtig zeigst, erscheint eine Animation auf dem Bildschirm." },
    ],
    exItems: [
      { icon: "❓", title: "Fragen", desc: "Eine Frage mit 4 möglichen Antworten wird angezeigt. Wähle die richtige Antwort. Richtig: +5 Pkt. Falsch: -1 Pkt." },
      { icon: "🔗", title: "Zuordnung", desc: "Bilder auf einer Seite, Texte auf der anderen. Tippe auf ein Bild und dann auf den passenden Text. Richtig: +1 Pkt. Falsch: -1 Pkt." },
      { icon: "🧩", title: "Puzzle", desc: "Ein Bild ist in 9 gemischte Teile zerlegt. Tippe auf zwei Teile, um sie zu tauschen. Stelle das vollständige Bild wieder zusammen." },
      { icon: "🃏", title: "Memory", desc: "Karten liegen verdeckt. Tippe auf zwei Karten, um sie umzudrehen. Finde alle Paare mit möglichst wenigen Zügen für eine hohe Punktzahl." },
      { icon: "📸", title: "Foto", desc: "Folge den Anweisungen und fotografiere den gewünschten Ort oder Gegenstand." },
    ],
  },
};

export default {
  name: "App",
  data() {
    return {
      isOpen: false,
      showInfo: false,
      activeSection: null,
    };
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    wazeLink() {
      return this.$store.getters.wazeLink;
    },
    t() {
      const lang = this.$store.getters.lang || 'fr';
      return TRANSLATIONS[lang] || TRANSLATIONS.fr;
    },
    isRtl() {
      const lang = this.$store.getters.lang || 'fr';
      return lang === 'he' || lang === 'ar';
    },
    hideNav() {
      return this.$route.meta && this.$route.meta.hideNav;
    },
  },
  methods: {
    reopenGame() {
      const navigationName = localStorage.getItem("navigationName");
      if (navigationName) {
        this.$router.push("/" + navigationName);
        location.reload();
      } else {
        this.$router.push("/");
      }
      this.isOpen = false;
    },
    restartGame() {
      const navigationName = localStorage.getItem("navigationName");
      const language = localStorage.getItem("language");
      localStorage.clear();
      if (language) localStorage.setItem("language", language);
      this.$router.push("/" + navigationName);
      this.isOpen = false;
    },
  },
};
</script>

<style media="screen" scoped>
@font-face { font-family: VarelaRound; src: url(../static/fonts/VarelaRound-Regular.ttf); }
@font-face { font-family: lithos;      src: url(/static/fonts/LithosPro-Regular.otf); }
@font-face { font-family: noot;        src: url(/static/fonts/Noot/noot-aj.woff); }
@font-face { font-family: segoe;       src: url(../static/fonts/segoe/SegoeUI.ttf); }
@font-face { font-family: DorianBook;   src: url(/static/fonts/dorian/dorian.woff) format('woff'),
                                            url(/static/fonts/dorian/dorianclm-book-webfont.ttf) format('truetype'); }
@font-face { font-family: DorianItalic; src: url(/static/fonts/dorian/dorianItalic.woff) format('woff'),
                                             url(/static/fonts/dorian/dorianclm-bookitalic-webfont.ttf) format('truetype'); }

/* ── Root ─────────────────────────────────────────────────── */
#mainDiv {
  direction: ltr;
  font-family: VarelaRound, sans-serif;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  max-width: 590px;
  margin: 0 auto;
  margin-bottom: 200px;
}
#mainDiv div, #mainDiv * { font-family: VarelaRound, sans-serif; }
#mainDiv::after {
  content: "";
  background-image: url("../static/img/bgGreen.png");
  background-color: white;
  opacity: 1;
  top: 0; left: 0; bottom: 0; right: 0;
  position: fixed;
  z-index: -1;
  filter: hue-rotate(93deg);
}
.rtl { direction: rtl; }
#app { color: rgb(211,211,211); font-family: VarelaRound, sans-serif; }

/* ── Navbar ───────────────────────────────────────────────── */
nav {
  background: white;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 10000;
  flex-wrap: wrap;
}
.navBarTitle {
  position: relative;
  margin: 0 auto;
  bottom: 2px;
}
.navBarTitle img { height: 54px; }
.navbar-waze {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}
.menu-trigger {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #1a1a1a;
  transition: background 0.15s;
}
.menu-trigger:hover { background: rgba(0,0,0,0.06); }

/* ── Dropdown menu ────────────────────────────────────────── */
.app-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 10500;
}
.app-menu-card {
  position: absolute;
  top: 68px;
  right: 12px;
  background: #1c1c1e;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px;
  min-width: 220px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
}
.app-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: none;
  background: transparent;
  color: #fff;
  font-family: VarelaRound, sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  text-align: right;
  transition: background 0.15s;
}
.app-menu-item:hover { background: rgba(255,255,255,0.08); }
.app-menu-icon { font-size: 20px; }
.app-menu-label { flex: 1; }
.app-menu-divider { height: 1px; background: rgba(255,255,255,0.1); margin: 0; }

/* ── Menu transition ──────────────────────────────────────── */
.menu-fade-enter-active { transition: opacity 0.15s, transform 0.15s; }
.menu-fade-leave-active { transition: opacity 0.1s, transform 0.1s; }
.menu-fade-enter-from, .menu-fade-leave-to { opacity: 0; transform: translateY(-8px); }

/* ── Info modal overlay ───────────────────────────────────── */
.info-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.75);
  z-index: 10600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.info-modal {
  background: #1c1c1e;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
}
.info-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  flex-shrink: 0;
}
.info-modal-title {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  font-family: VarelaRound, sans-serif;
}
.info-modal-close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.7);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.info-modal-close:hover { background: rgba(255,255,255,0.18); }
.info-modal-body {
  overflow-y: auto;
  padding: 12px 0 8px;
}

/* ── Accordion ────────────────────────────────────────────── */
.accordion-section { border-bottom: 1px solid rgba(255,255,255,0.08); }
.accordion-section:last-child { border-bottom: none; }
.accordion-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border: none;
  background: transparent;
  color: #fff;
  font-family: VarelaRound, sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  text-align: right;
  transition: background 0.15s;
  gap: 10px;
}
.accordion-trigger:hover { background: rgba(255,255,255,0.05); }
.accordion-trigger.open { background: rgba(255,255,255,0.05); }
.accordion-chevron {
  flex-shrink: 0;
  color: rgba(255,255,255,0.5);
  transition: transform 0.25s ease;
}
.accordion-chevron.rotated { transform: rotate(180deg); }
.accordion-content { padding: 0 16px 12px; }

/* ── Info items ───────────────────────────────────────────── */
.info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.info-item:last-child { border-bottom: none; }
.info-item-icon {
  font-size: 26px;
  flex-shrink: 0;
  margin-top: 2px;
}
.info-item-body { flex: 1; }
.info-item-title {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
  font-family: VarelaRound, sans-serif;
}
.info-item-desc {
  color: rgba(255,255,255,0.65);
  font-size: 13px;
  line-height: 1.5;
  font-family: VarelaRound, sans-serif;
}

/* ── Modal transition ─────────────────────────────────────── */
.modal-fade-enter-active { transition: opacity 0.2s; }
.modal-fade-leave-active { transition: opacity 0.15s; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

/* ── Accordion transition ─────────────────────────────────── */
.accordion-enter-active, .accordion-leave-active { transition: opacity 0.2s; }
.accordion-enter-from, .accordion-leave-to { opacity: 0; }

@media screen and (max-width: 600px) {}
</style>
